/**
 * Clients state for My Clients (Agency only).
 */
import { createSlice } from "@reduxjs/toolkit";

export interface ClientItem {
  id: string | number;
  /** Agency client user account id — matches requirement `client` when stored as user_id */
  user_id?: string | number | null;
  name?: string;
  company_name?: string;
  email?: string;
  industry?: string;
  status?: string;
  added?: string;
  [key: string]: unknown;
}

export interface ClientsCounts {
  total_clients: number;
  active_clients: number;
  inactive_clients: number;
  active_jobs?: number;
}

export interface ClientInfoState {
  data: any; // Using any for nested backend object representing client info + jobs
}

export interface ClientsState {
  list: ClientItem[];
  listTotal: number;
  loading: boolean;
  error: string | null;
  counts: ClientsCounts | null;
  countsLoading: boolean;
  createClientLoading: boolean;
  createClientError: string | null;
  clientInfo: ClientInfoState | null;
  clientInfoLoading: boolean;
  clientInfoError: string | null;
  editClientLoading: boolean;
  editClientError: string | null;
  changeStatusLoading: boolean;
  changeStatusError: string | null;
  activityLogs: any[];
  activityLogsLoading: boolean;
  activityLogsError: string | null;
}

const initialState: ClientsState = {
  list: [],
  listTotal: 0,
  loading: false,
  error: null,
  counts: null,
  countsLoading: false,
  createClientLoading: false,
  createClientError: null,
  clientInfo: null,
  clientInfoLoading: false,
  clientInfoError: null,
  editClientLoading: false,
  editClientError: null,
  changeStatusLoading: false,
  changeStatusError: null,
  activityLogs: [],
  activityLogsLoading: false,
  activityLogsError: null,
};

const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    setClientsList: (state, action) => {
      state.list = action.payload;
    },
    setClientsListTotal: (state, action) => {
      state.listTotal = action.payload;
    },
    setClientsLoading: (state, action) => {
      state.loading = action.payload;
    },
    setClientsError: (state, action) => {
      state.error = action.payload;
    },
    setClientsCounts: (state, action) => {
      state.counts = action.payload;
    },
    setClientsCountsLoading: (state, action) => {
      state.countsLoading = action.payload;
    },
    setCreateClientLoading: (state, action) => {
      state.createClientLoading = action.payload;
    },
    setCreateClientError: (state, action) => {
      state.createClientError = action.payload;
    },
    setClientInfo: (state, action) => {
      state.clientInfo = action.payload;
    },
    setClientInfoLoading: (state, action) => {
      state.clientInfoLoading = action.payload;
    },
    setClientInfoError: (state, action) => {
      state.clientInfoError = action.payload;
    },
    setEditClientLoading: (state, action) => {
      state.editClientLoading = action.payload;
    },
    setEditClientError: (state, action) => {
      state.editClientError = action.payload;
    },
    setChangeStatusLoading: (state, action) => {
      state.changeStatusLoading = action.payload;
    },
    setChangeStatusError: (state, action) => {
      state.changeStatusError = action.payload;
    },
    setActivityLogs: (state, action) => {
      state.activityLogs = action.payload;
    },
    setActivityLogsLoading: (state, action) => {
      state.activityLogsLoading = action.payload;
    },
    setActivityLogsError: (state, action) => {
      state.activityLogsError = action.payload;
    },
  },
});

export const {
  setClientsList,
  setClientsListTotal,
  setClientsLoading,
  setClientsError,
  setClientsCounts,
  setClientsCountsLoading,
  setCreateClientLoading,
  setCreateClientError,
  setClientInfo,
  setClientInfoLoading,
  setClientInfoError,
  setEditClientLoading,
  setEditClientError,
  setChangeStatusLoading,
  setChangeStatusError,
  setActivityLogs,
  setActivityLogsLoading,
  setActivityLogsError,
} = clientsSlice.actions;
export default clientsSlice.reducer;
